import { Controller } from "@hotwired/stimulus"

// "Share report / briefing" actions. Mints a gated public share link (POST to the endpoint),
// then either copies it to the clipboard (#share) or opens the user's email client with the
// link prefilled in the body (#email). The URL doesn't exist until minted.
//
//   <button data-controller="share-link"
//           data-share-link-endpoint-value="/account/share_links"
//           data-share-link-params-value='{"type":"report","company_id":42}'
//           data-share-link-subject-value="Acme — Inkling report"   (email only)
//           data-share-link-body-value="Here's the report:"          (email only)
//           data-action="share-link#share">Report link</button>
export default class extends Controller {
  static values = { endpoint: String, params: Object, subject: String, body: String }

  // Copy the minted link to the clipboard.
  async share(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const original = btn.innerHTML
    btn.disabled = true
    btn.textContent = "Creating link…"
    try {
      const res = await this.mint()
      if (res.ok) {
        const { url } = await res.json()
        await this.copy(url)
        btn.textContent = "Link copied ✓"
      } else if (res.status === 422) {
        const body = await res.json().catch(() => ({}))
        btn.textContent = body.error === "briefing_pending" ? "Briefing is being prepared" : "Nothing to share yet"
      } else {
        btn.textContent = "Couldn't create link"
      }
    } catch (_e) {
      btn.textContent = "Couldn't create link"
    } finally {
      setTimeout(() => { btn.innerHTML = original; btn.disabled = false }, 1800)
    }
  }

  // Mint the link, then open the user's email client with it prefilled in the body —
  // they just add a recipient and hit send.
  async email(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const original = btn.innerHTML
    const reset = (ms = 1800) => setTimeout(() => { btn.innerHTML = original; btn.disabled = false }, ms)
    btn.disabled = true
    btn.textContent = "Preparing…"
    try {
      const res = await this.mint()
      if (res.ok) {
        const { url } = await res.json()
        const subject = this.hasSubjectValue ? this.subjectValue : "Report"
        const intro = this.hasBodyValue ? this.bodyValue : "Here's the report:"
        const body = `${intro}\n\n${url}`
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
        btn.innerHTML = original
        btn.disabled = false
      } else if (res.status === 422) {
        const body = await res.json().catch(() => ({}))
        btn.textContent = body.error === "briefing_pending" ? "Briefing is being prepared" : "Nothing to share yet"; reset()
      } else {
        btn.textContent = "Couldn't create link"; reset()
      }
    } catch (_e) {
      btn.textContent = "Couldn't open email"; reset()
    }
  }

  mint() {
    return fetch(this.endpointValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify(this.paramsValue)
    })
  }

  async copy(text) {
    try {
      await navigator.clipboard.writeText(text)
    } catch (_e) {
      const ta = document.createElement("textarea")
      ta.value = text
      document.body.appendChild(ta); ta.select()
      try { document.execCommand("copy") } finally { ta.remove() }
    }
  }
}
